import type {ColumnBodyOptions} from "primereact/column";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber, type InputNumberProps} from "primereact/inputnumber";
import {Message} from "primereact/message";
import {Tooltip} from "primereact/tooltip";
import {type RefObject, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import type {RevaLanguage} from "@reva/shared/i18n";
import {formatArea} from "@reva/shared/number-format";
import {
    assetOutputColumns,
    FULL_OWNERSHIP_ASSET_TYPE,
    formatAssetValue,
    formatOwnershipShare,
} from "@reva/shared/register";
import type {RevaSpecObjectInputRow} from "@reva/shared/reva-spec";

import {ColumnHeaderWithIcons} from "../../../component/paramInput/ColumnHeaderWithIcons";
import {CellInputActionIcon} from "../../../component/paramInput/InputGrossFloorArea";
import InputTextField from "../../../component/paramInput/InputTextField";
import {
    useInputParameterDef,
    useMultipleParameterValues,
    useParameterValue,
} from "../../../component/paramInput/paramHooks";
import {ROW_ACTION_COLUMN_STYLE} from "../../../component/paramInput/RowDeleteIcon";
import {type SelectByCurrentLanguage, useLanguage, useSelectByCurrentLanguage} from "../../../hooks/languageHooks";
import {selectReadOnly} from "../../../redux/persistenceSlice";
import {selectEffectiveIndicationData} from "../../../redux/valueIndicationSelectors";
import {
    type AssetProblem,
    type AssetRow,
    affectedTotals,
    assetShareParameter,
    calculateTotalShare,
    calculateTotalSize,
    deleteAssetRow,
    findAssetProblems,
    isFieldDisabled,
    isLockedByParcel,
    PROBLEM_FIELDS,
    roundShares,
    shareParameter,
    sizeParameter,
    spaceParameter,
    typeParameter,
    updateAssetRow,
} from "./assetUtils";
import {RegisterOutputTable} from "./RegisterOutputTable";

const assetTypeList = [FULL_OWNERSHIP_ASSET_TYPE, "Wohnung/Teileigentum"];
/**
 * #19370: Bestandseinträge und die daraus berechneten Summen werden in EINEM Schreibvorgang persistiert,
 * damit die anschließende AVM-Berechnung bereits mit dem neuen Miteigentumsanteil läuft.
 * Muss eine stabile Referenz sein (useMultipleParameterValues).
 */
const assetsWithTotalsParameters = ["register_assets", spaceParameter, shareParameter];

type AssetTableRow = AssetRow & {newRow?: boolean};

interface SpecTexts {
    nameDe?: string | null;
    nameEn?: string | null;
    descriptionDe?: string | null;
    descriptionEn?: string | null;
}

export function RegisterAssetsInput({columns}: {columns: RevaSpecObjectInputRow[] | undefined}) {
    const {parameterValues: assetValues, onValueChange: setAssetData} = useParameterValue(
        "register_assets",
        "REGISTER",
    );
    const {onValuesChange: setAssetsWithTotals} = useMultipleParameterValues(assetsWithTotalsParameters, "REGISTER");
    const shareSpec = useInputParameterDef(shareParameter);
    const sizeSpec = useInputParameterDef(spaceParameter);
    const getByLanguage = useSelectByCurrentLanguage();
    const {t} = useTranslation();
    const language = useLanguage();
    const readOnly = useSelector(selectReadOnly);

    const assets: AssetRow[] = assetValues?.activeParameterValue || [];
    const rows: AssetTableRow[] = readOnly ? assets : [...assets, {newRow: true}];

    const updateValue = (newValue: unknown, field: string, index: number) => {
        const newAssets = updateAssetRow(assets, index, field, newValue);
        if (newAssets != null) {
            persistAssetsWithTotals(newAssets, field);
        }
    };

    /**
     * Die Kommentare der Summen werden wie beim Einzel-Update der berechneten Werte geleert.
     * @param newAssets vollständiger neuer Bestand ohne Platzhalterzeile
     * @param field geändertes Zeilenfeld; null = alle Summen neu berechnen (z.B. nach Zeile löschen)
     */
    function persistAssetsWithTotals(newAssets: AssetRow[], field: string | null) {
        const totals = affectedTotals(newAssets, field);
        const clearedComments = Object.fromEntries(Object.keys(totals).map((parameter) => [parameter, null]));
        setAssetsWithTotals(
            {register_assets: newAssets, ...totals},
            {register_assets: assetValues?.comment ?? null, ...clearedComments},
        );
    }

    const deleteRow = (index: number) => {
        persistAssetsWithTotals(deleteAssetRow(assets, index), null);
    };

    const addComment = (comment: string | null) => {
        void setAssetData(assetValues?.activeParameterValue, comment);
    };
    const onReset = (newValue: AssetRow[] | null) =>
        setAssetsWithTotals(
            {register_assets: newValue, [spaceParameter]: null, [shareParameter]: null},
            {register_assets: null, [spaceParameter]: null, [shareParameter]: null},
        );
    const typeColumn = columns?.find((column) => column.parameter === typeParameter);
    if (!columns || !typeColumn) return null;

    const problems = findAssetProblems(assets);
    const isProblemField = (index: number, field: string) =>
        problems.some((problem) => problem.rows.includes(index) && PROBLEM_FIELDS[problem.kind].includes(field));
    const problemText = (problem: AssetProblem) => {
        if (problem.kind === "total_share_exceeded") return t("object.tab_register.assets.total_share_exceeded");
        const target =
            problem.parcel === ""
                ? t("object.tab_register.assets.row", {row: problem.rows[0] + 1})
                : t("object.tab_register.assets.parcel", {parcel: problem.parcel});
        return t(`object.tab_register.assets.${problem.kind}`, {target});
    };

    const getColumnHeader = (column: RevaSpecObjectInputRow) =>
        getByLanguage(column.details?.nameDe ?? "", column.details?.nameEn ?? "");
    const firstColumnHeader = (
        <ColumnHeaderWithIcons
            onReset={onReset}
            title={getColumnHeader(typeColumn)}
            isActive={!readOnly}
            comment={assetValues?.comment}
            addComment={addComment}
            showReset={!!assetValues?.activeParameterValue}
            parameter={"register_assets"}
            parameterType="REGISTER"
            label={t("object.tab_register.title.assets")}
        />
    );

    return (
        <>
            <DataTable
                id={"input_register_assets"}
                className="p-inputtext-sm text-sm inputtext-compact register-table w-full"
                footer={assetTableFooter(assets, shareSpec, sizeSpec, language, getByLanguage)}
                value={rows}
                // Zellen-Closures hängen am Gesamtbestand, gemerkte Zellen würden mit veraltetem Stand speichern
                cellMemo={false}
                size="small"
            >
                {!readOnly && (
                    <Column
                        style={ROW_ACTION_COLUMN_STYLE}
                        body={(rowData: AssetTableRow, {rowIndex}: ColumnBodyOptions) => (
                            <CellInputActionIcon rowData={rowData} onDelete={() => deleteRow(rowIndex)} />
                        )}
                    />
                )}
                <Column
                    header={firstColumnHeader}
                    alignHeader="left"
                    headerClassName="w-full"
                    body={(rowData: AssetTableRow, {rowIndex}: ColumnBodyOptions) => (
                        <CellInputType
                            rowData={rowData}
                            disabled={readOnly}
                            invalid={isProblemField(rowIndex, typeParameter)}
                            onChange={(value) => updateValue(value, typeParameter, rowIndex)}
                        />
                    )}
                />
                {columns
                    .filter((column) => column !== typeColumn)
                    .map((column) => (
                        <Column
                            key={column.parameter}
                            header={getColumnHeader(column)}
                            alignHeader="right"
                            body={(rowData: AssetTableRow, {rowIndex}: ColumnBodyOptions) => (
                                <CellInput
                                    type={column?.details?.type}
                                    rowData={rowData}
                                    field={column.parameter}
                                    onChange={(value) => updateValue(value, column.parameter, rowIndex)}
                                    disabled={readOnly || isFieldDisabled(assets, rowIndex, column.parameter)}
                                    invalid={isProblemField(rowIndex, column.parameter)}
                                    tooltip={
                                        isLockedByParcel(assets, rowIndex, column.parameter)
                                            ? t("object.tab_register.assets.locked_by_parcel")
                                            : undefined
                                    }
                                />
                            )}
                        />
                    ))}
            </DataTable>
            {problems.map((problem) => (
                <Message
                    severity="error"
                    key={`${problem.kind}-${problem.rows[0] ?? ""}`}
                    className="w-full"
                    text={problemText(problem)}
                />
            ))}
        </>
    );
}

interface CellProps {
    rowData: AssetTableRow;
    disabled: boolean;
    invalid: boolean;
    onChange: (value: unknown) => void;
}

function CellInputType({rowData, disabled, invalid, onChange}: CellProps) {
    return (
        <Dropdown
            value={rowData[typeParameter]}
            disabled={disabled}
            className="w-full"
            options={assetTypeList}
            onChange={(e) => onChange(e.value)}
            invalid={invalid || (!rowData.newRow && !rowData[typeParameter])}
        />
    );
}

interface FieldCellProps extends CellProps {
    field: string;
    tooltip: string | undefined;
}

function CellInput({type, ...props}: FieldCellProps & {type?: string}) {
    if (type === "integer" || type === "double") return <CellInputNumber {...props} />;
    if (type !== "string") return null;
    return (
        <InputTextField
            onChange={(e) => props.onChange(e.target.value)}
            value={props.rowData[props.field]}
            className="!w-20"
            disabled={props.disabled}
        />
    );
}

const assetTableFooter = (
    assets: AssetRow[],
    shareSpec: SpecTexts | null,
    sizeSpec: SpecTexts | null,
    language: RevaLanguage,
    getByLanguage: SelectByCurrentLanguage,
) => {
    const share = calculateTotalShare(assets);
    const size = calculateTotalSize(assets);
    const shareValue = share === 0 || shareSpec == null ? "" : formatOwnershipShare(share, size, language);
    const sumValue = size ? formatArea(size, language) : "";
    const sumLabel = getByLanguage(`${sizeSpec?.nameDe} insgesamt:`, `${sizeSpec?.nameEn} total:`);
    const shareLabel = getByLanguage(`${shareSpec?.nameDe} insgesamt:`, `${shareSpec?.nameEn} total:`);
    const sumTooltip = getByLanguage(sizeSpec?.descriptionDe ?? "", sizeSpec?.descriptionEn ?? "");
    const shareTooltip = getByLanguage(shareSpec?.descriptionDe ?? "", shareSpec?.descriptionEn ?? "");

    return (
        <div className="font-bold w-full flex flex-row items-center inputtext-compact">
            <span className="text-right flex-1 mr-2 footer_tooltip" data-pr-tooltip={shareTooltip}>
                {shareLabel}
            </span>
            <span className="text-center mx-4 footer_tooltip" data-pr-tooltip={shareTooltip}>
                {shareValue}
            </span>
            <span className="text-center mx-2 footer_tooltip" data-pr-tooltip={sumTooltip}>
                {sumLabel}
            </span>
            <span className="text-center ml-4 footer_tooltip" data-pr-tooltip={sumTooltip}>
                {sumValue}
            </span>
            <Tooltip target="#input_register_assets .footer_tooltip" />
        </div>
    );
};

const numberInputProps: Record<string, InputNumberProps> = {
    [sizeParameter]: {suffix: " m²", className: "!w-32", useGrouping: true, minFractionDigits: 2, maxFractionDigits: 2},
    // #18295: mehr Nachkommastellen eingeben lassen, gespeichert wird kaufmännisch auf 3 gerundet
    [assetShareParameter]: {className: "!w-24", useGrouping: true, minFractionDigits: 1, maxFractionDigits: 6},
};
const defaultNumberInputProps: InputNumberProps = {
    className: "!w-24",
    useGrouping: true,
    minFractionDigits: 0,
    maxFractionDigits: 0,
};

function CellInputNumber({rowData, field, disabled, invalid, tooltip, onChange}: FieldCellProps) {
    const language = useLanguage();
    const inputRef = useRef<InputNumber>(null);
    const wrapperRef = useRef<HTMLSpanElement>(null);

    const handleValueChange = (e: {value?: number | null}) => {
        if (field !== assetShareParameter) return onChange(e.value);
        const rounded = roundShares(e.value);
        const input = inputRef.current?.getInput() as unknown as HTMLInputElement | null;
        if (rounded !== e.value && rounded === rowData[field] && input) {
            // Unveränderter Wert rendert InputNumber nicht neu, die Eingabe bliebe ungerundet stehen
            input.value = inputRef.current?.getFormatter().format(rounded);
        }
        onChange(rounded);
    };

    const currentValue = (rowData[field] as number | null | undefined) ?? null;
    const input = (
        <InputNumber
            {...(numberInputProps[field] ?? defaultNumberInputProps)}
            ref={inputRef}
            value={currentValue}
            locale={language}
            inputClassName="w-full text-right"
            onValueChange={handleValueChange}
            disabled={disabled}
            invalid={invalid || (!rowData.newRow && currentValue == null)}
            min={0}
        />
    );
    if (!tooltip) return input;
    return (
        <>
            <span ref={wrapperRef}>{input}</span>
            <Tooltip
                target={wrapperRef as RefObject<HTMLElement>}
                content={tooltip}
                position="top"
                className="custom-tooltip"
            />
        </>
    );
}

export function RegisterOutputAssets({columns}: {columns: RevaSpecObjectInputRow[]}) {
    const {
        parameterValues: {activeParameterValue: assets},
    } = useParameterValue("register_assets", "REGISTER");
    const shareSpec = useInputParameterDef(shareParameter);
    const language = useLanguage();

    return (
        <RegisterOutputTable
            columns={assetOutputColumns(columns, shareSpec ? assets : null)}
            values={assets || []}
            formatValue={(parameter, value) => formatAssetValue(parameter, value, language)}
        />
    );
}

/**
 * Grundstücksfläche und Miteigentumsanteil für die Ausgabe unter Grundbuchblatt (#19375-Folge), wie im
 * PDF aus der Antwort der führenden Indikation: Das sind die Werte, mit denen gerechnet wurde, auch
 * wenn sie auf Überblick/Grundstück abweichend von den Summen des Bestandsverzeichnisses gesetzt sind.
 */
export function useRegisterAssetTotals(): {name: string; value: string}[] {
    const indicationData = useSelector(selectEffectiveIndicationData);
    const totalSize = indicationData?.[spaceParameter] as number | null | undefined;
    const totalShare = indicationData?.[shareParameter] as number | null | undefined;
    const sizeSpec = useInputParameterDef(spaceParameter);
    const shareSpec = useInputParameterDef(shareParameter);
    const {t} = useTranslation();
    const language = useLanguage();
    const getByLanguage = useSelectByCurrentLanguage();

    const noEntry = t("object.tab_register.no_entry");
    const size = {
        name: getByLanguage(sizeSpec?.nameDe ?? "", sizeSpec?.nameEn ?? ""),
        value: formatArea(totalSize, language) || noEntry,
    };
    if (!shareSpec) return [size];
    return [
        size,
        {
            name: getByLanguage(shareSpec.nameDe ?? "", shareSpec.nameEn ?? ""),
            value: totalShare == null ? noEntry : formatOwnershipShare(totalShare, totalSize, language),
        },
    ];
}
